// ENEMY ERAS (owner 2026-11-03: 'reuse the same enemies, but at different times the difficulty increases all around ...
// everybody coming in should be at a completely different point'). Pure data + helpers; Sim stamps the era on each foe at
// spawn (Enemy.era) and applies these multipliers from it. Ordinary foes and elites only: bosses, guardians and the herald
// stay at era 0. Deterministic: no rng here.

export const ERA_SECONDS = 300, ERA_REGULAR_MAX = 6, ERA_MAX = 9;
export const ERA_NAMES = ['', 'Bronze', 'Iron', 'Silver', 'Gold', 'Crimson', 'Umbral', 'Void', 'Starfall', 'Apex'] as const;
export const eraName = (era: number): string => ERA_NAMES[Math.max(0, Math.min(ERA_MAX, era | 0))];

/** the era at scheduled time t (Hurry-aware: pass sim.schedTime); regular timeline caps at VI (30:00), Endless adds +1 per
 *  5 min of Endless time up to IX */
export function eraAt(t: number, endlessSeconds = 0): number {
  const base = Math.min(ERA_REGULAR_MAX, Math.floor(Math.max(0, t) / ERA_SECONDS));
  return Math.min(ERA_MAX, base + (endlessSeconds > 0 ? Math.floor(endlessSeconds / ERA_SECONDS) : 0));
}

/** movement speed (the sim's speed cap is raised by the same factor, but stays under the hero's base speed) */
export const eraSpeedMul = (era: number): number => 1 + 0.05 * era;
/** attack / ability cooldowns (stomper, lancer): shorter each era, floor 0.6 */
export const eraCdMul = (era: number): number => Math.max(0.6, 1 - 0.06 * era);
/** contact damage (on top of enemyDmgMul) */
export const eraDmgMul = (era: number): number => 1 + 0.1 * era;
/** knockback impulse (on top of lateKnockMul) */
export const eraKnockMul = (era: number): number => 1 / (1 + 0.15 * era);
/** body size */
export const eraSizeMul = (era: number): number => 1 + 0.05 * era;

/** Composition: the weakest packs (drone, skitter, wisp packs) fall to 25% of their era-0 share by era 4 */
export const eraWeakMul = (era: number): number => Math.max(0.25, 1 - 0.1875 * era);

// Era-0 spawn shares once every kind is unlocked (the cumulative thresholds of the classic roll: .22 .30 .38 .45 .52 .58 .63 .67)
const SKITTER = 0.22, DRONE = 1 - 0.67, BOMBER = 0.07;
const FED: ReadonlyArray<readonly [string, number]> = [['lancer', 0.08], ['caster', 0.08], ['brute', 0.07], ['splitter', 0.06], ['stomper', 0.05], ['warden', 0.04]];
const FED_SUM = 0.38;

export type EraKind = 'drone' | 'skitter' | 'lancer' | 'caster' | 'bomber' | 'brute' | 'splitter' | 'stomper' | 'warden';
/** The kind roll for era >= 1 (every kind is unlocked by then): same single uniform r as the classic cascade, only the
 *  thresholds move. Era 0 keeps the original cascade in Sim (bit-identical). */
export function eraKindRoll(r: number, era: number): EraKind {
  const w = eraWeakMul(era), freed = (SKITTER + DRONE) * (1 - w);
  let acc = SKITTER * w;
  if (r < acc) return 'skitter';
  const order: ReadonlyArray<readonly [EraKind, number]> = [
    ['lancer', FED[0][1]], ['caster', FED[1][1]], ['bomber', BOMBER], ['brute', FED[2][1]], ['splitter', FED[3][1]], ['stomper', FED[4][1]], ['warden', FED[5][1]],
  ];
  for (const [k, share] of order) {
    acc += k === 'bomber' ? share : share + freed * (share / FED_SUM);
    if (r < acc) return k;
  }
  return 'drone';
}
